import React from "react";
import { useTranslation } from "react-i18next";
import { commitVisit, resolveVisit, type VisitResolve } from "@/lib/services/visitPublicService";

/**
 * ?action=visit&token=… landing — the confirm / cancel link a Boona Direct
 * SMS carries instead of buttons (inbox plan INB5 in the backend repo).
 * The GET is a pure read (SMS apps prefetch links); nothing changes until
 * the client presses Confirm, or Cancel and then confirms it. Shown INSTEAD
 * of the booking flow, like the reminders opt-out page.
 */
export default function VisitActionView({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [visit, setVisit] = React.useState<VisitResolve | null>(null);
  const [state, setState] = React.useState<"loading" | "ready" | "invalid" | "error">("loading");
  const [askCancel, setAskCancel] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    resolveVisit(token)
      .then((res) => { if (alive) { setVisit(res); setState("ready"); } })
      .catch((e) => {
        if (!alive) return;
        setState((e as any)?.response?.status === 404 ? "invalid" : "error");
      });
    return () => { alive = false; };
  }, [token]);

  const act = async (action: "confirm" | "cancel") => {
    setBusy(true);
    try {
      setVisit(await commitVisit(token, action));
      setAskCancel(false);
    } catch (e) {
      setState((e as any)?.response?.status === 404 ? "invalid" : "error");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div
      className="font-sans"
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "50vh", padding: "2rem 1rem",
        textAlign: "center", color: "#374151",
      }}
    >
      {children}
    </div>
  );
  const title = (text: string) => (
    <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: ".25rem 0 .75rem" }}>{text}</h2>
  );
  const button = (label: string, onClick: () => void, color: string, outline = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        background: outline ? "transparent" : color, color: outline ? color : "#ffffff",
        border: `2px solid ${color}`, borderRadius: 8, padding: "0.7rem 1.5rem",
        fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
        minWidth: 150,
      }}
    >
      {label}
    </button>
  );

  if (state === "loading") return shell(<div>{t("widget.visit_action.loading", "One moment…")}</div>);
  if (state === "invalid") {
    return shell(
      <>
        {title(t("widget.visit_action.invalid_title", "This link is no longer valid"))}
        <p style={{ maxWidth: 420, color: "#6b7280" }}>
          {t("widget.visit_action.invalid_body",
            "The visit could not be found or the link has expired. Please contact the salon.")}
        </p>
      </>,
    );
  }
  if (state === "error" || !visit) {
    return shell(
      <p style={{ maxWidth: 420, color: "#6b7280" }}>
        {t("widget.visit_action.error", "Something went wrong — please try the link again in a minute.")}
      </p>,
    );
  }

  const when = (() => {
    if (!visit.date) return "";
    const [y, m, d] = visit.date.split("-").map(Number);
    const day = new Intl.DateTimeFormat(i18n.language || undefined, {
      weekday: "long", day: "numeric", month: "long",
    }).format(new Date(y, m - 1, d));
    return visit.time ? `${day}, ${visit.time}` : day;
  })();
  const card = (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 12, padding: "1rem 1.25rem", margin: "0 0 1.25rem",
      maxWidth: 420, width: "100%", textAlign: "left",
    }}>
      <div style={{ fontWeight: 700 }}>{visit.company_title}</div>
      <div style={{ marginTop: 4 }}>{when}</div>
      {visit.services.length > 0 && <div style={{ color: "#6b7280", marginTop: 4 }}>{visit.services.join(", ")}</div>}
      {visit.master_name && <div style={{ color: "#6b7280", marginTop: 2 }}>{visit.master_name}</div>}
    </div>
  );

  if (visit.state === "cancelled") {
    return shell(<>{title(t("widget.visit_action.cancelled_title", "The visit is cancelled"))}{card}
      <p style={{ maxWidth: 420, color: "#6b7280" }}>
        {t("widget.visit_action.cancelled_body", "The salon has been notified. You can book another time whenever you like.")}
      </p></>);
  }
  if (visit.state === "past") {
    return shell(<>{title(t("widget.visit_action.past_title", "This visit has already passed"))}{card}</>);
  }
  if (askCancel) {
    return shell(
      <>
        {title(t("widget.visit_action.cancel_confirm_title", "Cancel this visit?"))}
        {card}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          {button(t("widget.visit_action.cancel_yes", "Yes, cancel"), () => act("cancel"), "#e11d48")}
          {button(t("widget.visit_action.cancel_no", "Keep the visit"), () => setAskCancel(false), "#374151", true)}
        </div>
      </>,
    );
  }
  const confirmed = visit.state === "confirmed";
  return shell(
    <>
      {title(confirmed
        ? t("widget.visit_action.confirmed_title", "Your visit is confirmed")
        : t("widget.visit_action.pending_title", "Will you make it?"))}
      {card}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        {!confirmed && button(t("widget.visit_action.confirm", "Confirm"), () => act("confirm"), "#16a34a")}
        {button(t("widget.visit_action.cancel", "Cancel the visit"), () => setAskCancel(true), "#e11d48", true)}
      </div>
    </>,
  );
}
